/**
 * The refresh reveal (GLOSSARY.md "Refresh reveal") — the card flip a
 * committed Daily Picks refresh plays: every card turns face-down to the
 * "?" card, which breathes while the new assortment is awaited; the new
 * assortment lands behind them, and each new card turns face-up in display
 * order, landing with a burst of sparkle.
 *
 * The timings live here alone. The flow drives its phases off them and the
 * card faces animate with them, so the pending state can never end before
 * the last card has landed — and it ends at the same moment whether the
 * cards animated or swapped. The 400/60 flip defaults stand until a motion
 * spec supplies real ones (#407); the breath and the sparkle follow the
 * designer's spec for those two beats.
 */

import { canAnimate, trackKeyframes, type MotionTrack } from './motion'

export const FLIP_DURATION_MS = 400
export const FLIP_STAGGER_MS = 60
// One breath of the face-down card's loading pulse, peak to peak.
export const BREATH_CYCLE_MS = 1000
const BREATH_EASING = 'cubic-bezier(0.45, 0, 0.55, 1)'
// The sparkle breaks this far before the card's turn face-up completes, so
// the light and the landing read as one beat, and runs this long.
const SPARKLE_LEAD_MS = 80
export const SPARKLE_DURATION_MS = 450

// How long a whole grid takes to turn over: the last card waits one stagger
// step per slot ahead of it, then takes the full flip. Slots keep their
// display position in the rhythm whether they render or not — an omitted
// slot leaves a gap in the deal rather than closing it up.
export function flipSpanMs(slotCount: number): number {
  if (slotCount <= 0) return 0
  return FLIP_DURATION_MS + FLIP_STAGGER_MS * (slotCount - 1)
}

// The shortest face-down phase, measured from the commit: the whole grid's
// turn-over and one full breath — what a server that has answered inside it
// gets, so a fast refetch still shows the "?" cards breathing together, a
// flip rather than a flicker. The flow breathes on in whole cycles past
// this until the new assortment has landed. An assortment with nothing to
// turn still holds for the breath.
export function faceDownHoldMs(slotCount: number): number {
  return flipSpanMs(slotCount) + BREATH_CYCLE_MS
}

// One card's turn, on the Web Animations API. The element's own style
// already holds the face it is turning to, so the animation only has to
// cover the way there: `fill: 'backwards'` keeps the card on its old face
// through its stagger delay, and letting it fall back to that style at the
// end leaves nothing to clean up.
function turnCardFace(
  element: HTMLElement,
  faceDown: boolean,
  index: number,
): Animation | undefined {
  if (!canAnimate(element)) return
  return element.animate(
    [
      { transform: faceDown ? 'rotateY(0deg)' : 'rotateY(180deg)' },
      { transform: faceDown ? 'rotateY(180deg)' : 'rotateY(0deg)' },
    ],
    {
      delay: index * FLIP_STAGGER_MS,
      duration: FLIP_DURATION_MS,
      easing: 'ease-in-out',
      fill: 'backwards',
    },
  )
}

// The face-down card's loading pulse: opacity between 1 and 0.4 with a 3%
// vertical swell, half a cycle each way on a sine-like curve, from the peak
// and until stopped. The flow starts every card's in the same tick and stops
// them at a cycle's end, so the grid breathes in step and comes to rest at
// the peak it started from. `scale` rather than `transform`: the face's own
// transform is the half-turn that puts it behind the card, and the swell
// must compose with it, not replace it.
function breatheFaceDown(face: HTMLElement): Animation | undefined {
  if (!canAnimate(face)) return
  return face.animate(
    [
      { opacity: 1, scale: '1 1' },
      { opacity: 0.4, scale: '1 1.03' },
    ],
    {
      direction: 'alternate',
      duration: BREATH_CYCLE_MS / 2,
      easing: BREATH_EASING,
      iterations: Infinity,
    },
  )
}

// One particle of the sparkle a new card lands with: its shape and size, and
// where it appears and scatters to, in px from the card's centre (y down).
export interface RevealSparkle {
  shape: 'circle' | 'square'
  size: number
  from: readonly [number, number]
  to: readonly [number, number]
}

// Five white particles around the card's centre — one straight above, two
// above to either side, one to each side — three round and two square, 7 to
// 10px, each scattering 18 to 30px outward. The spec gives the counts,
// sizes, distances, and the rough placement; these coordinates are the one
// reading of it — mirrored placements about the card's vertical axis, with
// the sizes and distances varied inside the spec's ranges so the burst does
// not read as a stamp.
export const REVEAL_SPARKLES: readonly RevealSparkle[] = [
  { shape: 'circle', size: 10, from: [0, -36], to: [0, -66] },
  { shape: 'square', size: 8, from: [-26, -26], to: [-43, -43] },
  { shape: 'square', size: 8, from: [26, -26], to: [43, -43] },
  { shape: 'circle', size: 7, from: [-40, -4], to: [-60, -4] },
  { shape: 'circle', size: 9, from: [40, -4], to: [62, -4] },
]

// When a card at this display position sparkles, measured like its turn
// from the moment the grid is told to turn face-up.
export function sparkleDelayMs(index: number): number {
  return index * FLIP_STAGGER_MS + FLIP_DURATION_MS - SPARKLE_LEAD_MS
}

// The burst's beats, as shares of its 450ms: full within 50ms, settled to
// 0.9 by 100ms, held to 250ms — where the swell peaks — then fading and
// shrinking away.
const SPARKLE_FLARE = 50 / SPARKLE_DURATION_MS
const SPARKLE_SETTLE = 100 / SPARKLE_DURATION_MS
const SPARKLE_PEAK = 250 / SPARKLE_DURATION_MS
const SPARKLE_FADE_EASING = 'cubic-bezier(0.4, 0, 1, 1)'
const SPARKLE_SWELL_EASING = 'cubic-bezier(0.22, 1.3, 0.36, 1)'
const SPARKLE_SCATTER_EASING = 'cubic-bezier(0, 0, 0.3, 1)'

// One particle's four tracks. Opacity starts and ends at 0 over the
// particle's invisible base, so a finished burst leaves nothing to clean
// up; the swell overshoots on its spring-like curve before collapsing to a
// fifth; the scatter leaves fast and settles; squares turn further than
// circles, whose spin only shows through their motion.
function sparkleTracks(sparkle: RevealSparkle): MotionTrack[] {
  return [
    {
      property: 'opacity',
      values: [0, 1, 0.9, 0.9, 0],
      times: [0, SPARKLE_FLARE, SPARKLE_SETTLE, SPARKLE_PEAK, 1],
      easing: ['linear', 'linear', 'linear', SPARKLE_FADE_EASING],
    },
    {
      property: 'scale',
      values: [0, 1.8, 0.2],
      times: [0, SPARKLE_PEAK, 1],
      easing: [SPARKLE_SWELL_EASING, SPARKLE_FADE_EASING],
    },
    {
      property: 'translate',
      values: [
        `${sparkle.from[0]}px ${sparkle.from[1]}px`,
        `${sparkle.to[0]}px ${sparkle.to[1]}px`,
      ],
      times: [0, 1],
      easing: SPARKLE_SCATTER_EASING,
    },
    {
      property: 'rotate',
      values: ['0deg', sparkle.shape === 'square' ? '120deg' : '60deg'],
      times: [0, 1],
      easing: 'linear',
    },
  ]
}

/** One WAAPI keyframe list per track, ready for `element.animate`. */
export function sparkleAnimations(sparkle: RevealSparkle): Keyframe[][] {
  return sparkleTracks(sparkle).map(trackKeyframes)
}

// The burst over one card landing face-up: each particle element plays the
// tracks of its `REVEAL_SPARKLES` entry, by position, from the card's
// sparkle delay. Particles beyond the spec's five, or the spec's beyond the
// mounted particles, are simply not played.
function sparkle(particles: readonly HTMLElement[], index: number): void {
  particles.forEach((particle, position) => {
    const spec = REVEAL_SPARKLES[position]
    if (!spec || !canAnimate(particle)) return
    for (const keyframes of sparkleAnimations(spec)) {
      particle.animate(keyframes, {
        delay: sparkleDelayMs(index),
        duration: SPARKLE_DURATION_MS,
      })
    }
  })
}

// The DOM one Daily Picks slot's reveal animates (card-flip.tsx mounts it):
// the container that turns between the faces, the "?" face behind the card,
// and the sparkle's particles laid over both, in `REVEAL_SPARKLES` order.
export interface SlotElements {
  card: HTMLElement
  back: HTMLElement
  sparkles: readonly HTMLElement[]
}

export interface SlotMotion {
  // The flow's breathing beat (GLOSSARY.md "Refresh reveal"): on, the "?"
  // face pulses; off, it comes to rest. Idempotent either way, so a card
  // already breathing keeps its breath.
  breathe: (breathing: boolean) => void
  // Stop everything still running — the slot is leaving the screen.
  dispose: () => void
  // Turn the card to the given face from the other, at its display
  // position's place in the deal.
  turn: (faceDown: boolean, index: number) => void
}

// One slot's motion through the reveal. The breath is the one thing with a
// lifetime, so it is owned here, on the flow's say-so: the flow breathes the
// whole grid once the last card has landed face-down and rests it at a
// cycle's end, just before the new assortment is dealt, so the face never
// snaps mid-pulse. A slot that unmounts mid-breath takes its breath with it.
// The turn and the sparkle are fire-and-forget: they play and end on their
// own, the sparkle invisible.
export function createSlotMotion(elements: SlotElements): SlotMotion {
  let breath: Animation | undefined
  const stopBreathing = () => {
    breath?.cancel()
    breath = undefined
  }
  return {
    breathe: (breathing) => {
      if (breathing) breath ??= breatheFaceDown(elements.back)
      else stopBreathing()
    },
    dispose: stopBreathing,
    turn: (faceDown, index) => {
      turnCardFace(elements.card, faceDown, index)
      if (!faceDown) sparkle(elements.sparkles, index)
    },
  }
}
